import { Component } from '@theme/component';

/**
 * GOOMY · Línea de tiempo "¿Qué pasa en tu cuerpo?".
 *
 * A medida que el usuario hace scroll, la línea vertical (.btl__fill) se rellena
 * y los marcadores (.btl__marker) se activan al ser alcanzados. El progreso se
 * calcula con scroll/resize para funcionar en todos los navegadores.
 */
class DeilyTimeline extends Component {
  connectedCallback() {
    super.connectedCallback?.();

    this.timeline = this.querySelector('.btl__timeline');
    this.markers = Array.from(this.querySelectorAll('.btl__marker'));
    this.slots = Array.from(this.querySelectorAll('.btl__slot[data-step]'));
    this.currentSlot = null;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.#update();
        this.ticking = false;
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.#update();

    this.#setupReveal();
    this.#setupAccordion();
  }

  // En celular cada semana es un desplegable: la cabecera (.btl__week) abre/cierra
  // su contenido. En escritorio el handler no hace nada (la maquetación es la de
  // siempre y el CSS de colapso solo existe bajo el media query de celular).
  #setupAccordion() {
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    const heads = Array.from(this.querySelectorAll('.btl__week'));

    for (const head of heads) {
      const item = head.closest('.btl__item');
      if (!item) continue;
      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');
      head.setAttribute('aria-expanded', 'false');

      const toggle = () => {
        if (!this.mobileQuery.matches) return;
        const open = item.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      head.addEventListener('click', toggle);
      head.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    }
  }

  // Animación de entrada: revela intro, imagen y cada etapa al asomar en pantalla.
  // Solo se activa si hay soporte y el usuario no pidió reducir movimiento; si no,
  // todo queda visible por defecto (el estado oculto vive bajo la clase .is-anim).
  #setupReveal() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;

    this.classList.add('is-anim');
    const targets = this.querySelectorAll('.btl__intro, .btl__media, .btl__item');
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.2 }
    );
    targets.forEach((el) => io.observe(el));
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  #update() {
    if (!this.timeline) return;

    const rect = this.timeline.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const top = 8;

    // La línea va del primer marcador al último: NO continúa después del último punto.
    let trackLen = rect.height - 16;
    const last = this.markers[this.markers.length - 1];
    if (last) {
      const lr = last.getBoundingClientRect();
      trackLen = lr.top + lr.height / 2 - rect.top - top;
    }
    if (trackLen < 0) trackLen = 0;
    this.timeline.style.setProperty('--track-len', trackLen.toFixed(1) + 'px');

    // La línea se llena a medida que este punto de anclaje recorre la línea de
    // tiempo. Más alto (más cerca de 1) = cada imagen cambia antes al scrollear.
    const anchor = vh * 0.68;
    let progress = (anchor - rect.top - top) / (trackLen || 1);
    progress = Math.max(0, Math.min(1, progress));

    this.timeline.style.setProperty('--progress', progress.toFixed(4));

    const fillPx = top + progress * trackLen;
    let activeIndex = -1;
    for (let i = 0; i < this.markers.length; i++) {
      const mRect = this.markers[i].getBoundingClientRect();
      const center = mRect.top + mRect.height / 2 - rect.top;
      const isActive = center <= fillPx + 1;
      this.markers[i].classList.toggle('is-active', isActive);
      if (isActive) activeIndex = i;
    }

    this.#showSlot(activeIndex);
  }

  // Muestra la imagen central de la semana activa (cross-fade). Si la semana no
  // tiene imagen propia (o ninguna está activa), queda visible la imagen base.
  #showSlot(index) {
    if (!this.slots || this.slots.length === 0 || this.currentSlot === index) return;
    this.currentSlot = index;
    for (const slot of this.slots) {
      slot.classList.toggle('is-shown', Number(slot.dataset.step) === index);
    }
  }
}

if (!customElements.get('deily-timeline')) {
  customElements.define('deily-timeline', DeilyTimeline);
}
